import {
    PublicClientApplication,
    EventType,
    PopupRequest,
    RedirectRequest,
    SilentRequest,
    InteractionType,
    AccountInfo,
    IPublicClientApplication,
    InteractionRequiredAuthError,
} from "@azure/msal-browser";
import { ADConfig } from "@/config/auth";

import { ApiService } from "./api-service";
import { onExceptionGetMessage } from "@/utils/errorHandling";
import { MSAL_REDIRECT_PAGE_URL } from "@/constants";

/**
 * MSAL authentication request options.
 */
interface IAuthRequestOptions {
    /**
     * Optional additional scopes for the authentication request.
     */
    scopes?: string[];
    interactionType: InteractionType;
}

/**
 * Authentication service for Microsoft Authentication Library.
 */
class AuthService {
    static adConfig: ADConfig;
    /**
     * Creates an MSAL public client application instance.
     * @param config Optional custom config. If not specified the default config will be used.
     * @returns An MSAL public client application instance.
     */
    static async createPublicClientApplication(
        config: ADConfig,
    ): Promise<PublicClientApplication> {
        if (this.adConfig) throw new Error("ADConfig is already set");
        this.adConfig = config;
        const publicClientApp = new PublicClientApplication(config.msal);
        await publicClientApp.initialize();
        // Account selection logic is app dependent. Adjust as needed for different use cases.
        const accounts = publicClientApp.getAllAccounts();

        if (accounts.length > 0) {
            const account: AccountInfo = accounts[0];

            publicClientApp.setActiveAccount(account);
        }

        publicClientApp.addEventCallback((message) => {
            const payload = message.payload;

            switch (message.eventType) {
                case EventType.LOGIN_SUCCESS: {
                    const account = payload as AccountInfo | null;
                    if (account) {
                        publicClientApp.setActiveAccount(account);
                    }
                    const signupCode = localStorage.getItem("signupCode");
                    if (signupCode && config.id == "adb2c")
                        this.acceptSignupCode(publicClientApp, signupCode);
                    break;
                }
                case EventType.LOGOUT_START:
                case EventType.LOGOUT_SUCCESS:
                    localStorage.removeItem("signupCode");
                    break;
            }
        });

        return publicClientApp;
    }
    /**
     * Accept a signup code for an ADB2C account.
     * User has just signed up. Call our acceptSignupCode endpoint to associate the pending code (from local storage)
     * with the new account (in usual auth header).
     * On success, reload the page (sans code). On error, show the message within the signup page.
     * @param instance
     * @param signupCode
     */
    static async acceptSignupCode(
        instance: IPublicClientApplication,
        signupCode: string,
    ) {
        let uri = `/signup`;
        try {
            const api = await ApiService.createMyAccountApiAsync(
                undefined,
                instance,
            );
            await api.acceptSignupCode({ code: signupCode });
        } catch (error) {
            const errorMessage = await onExceptionGetMessage(error);
            uri += `?error=${encodeURIComponent(errorMessage)}`;
        } finally {
            localStorage.removeItem("signupCode");
            window.location.href = uri;
        }
    }

    /**
     * Creates a MSAL authentication request.
     * @param options The authentication request options.
     * @param defaultScopes The optional default scopes. If specified it will override the defaults from config.
     * @returns An MSAL authentication request.
     */
    static createAuthRequest(
        options: IAuthRequestOptions = {
            interactionType: InteractionType.Redirect,
        },
        defaultScopes = this.adConfig?.scopes,
    ): PopupRequest | RedirectRequest | SilentRequest {
        let scopes: string[];

        if (options.scopes && options.scopes.length > 0) {
            scopes = [...options.scopes, ...defaultScopes];
        } else {
            scopes = defaultScopes;
        }

        if (options.interactionType === InteractionType.Popup) {
            const request: PopupRequest = { scopes: scopes };

            return request;
        } else if (options.interactionType === InteractionType.Redirect) {
            const request: RedirectRequest = { scopes: scopes };

            return request;
        } else if (options.interactionType === InteractionType.Silent) {
            const request: SilentRequest = { scopes: scopes };

            return request;
        } else {
            throw Error("Invalid interaction type.");
        }
    }

    static async getAuthorizationHeader(
        instance: IPublicClientApplication,
    ): Promise<string> {
        const accessToken = await AuthService.getAuthorizationToken(instance);
        return `Bearer ${accessToken}`;
    }

    static async getAuthorizationToken(
        instance: IPublicClientApplication,
    ): Promise<string> {
        if (!instance) {
            throw new Error("Undefined msal instance");
        }
        const request = AuthService.createAuthRequest({
            interactionType: InteractionType.Silent,
        });

        if (import.meta.env["STORYBOOK"]) {
            return "";
        }
        try {
            await instance.handleRedirectPromise({
                navigateToLoginRequestUrl: true,
            });
            const silentRequest = {
                ...request,
                redirectUri: `${window.location.origin}${MSAL_REDIRECT_PAGE_URL}`,
            };
            const response = await instance.acquireTokenSilent(silentRequest);
            return response.accessToken;
        } catch (error) {
            if (error instanceof InteractionRequiredAuthError) {
                await instance.handleRedirectPromise({
                    navigateToLoginRequestUrl: true,
                });
                await instance.acquireTokenRedirect(
                    AuthService.createAuthRequest({
                        interactionType: InteractionType.Redirect,
                    }),
                );
                return "";
            }

            throw error;
        }
    }
}

export default AuthService;
